// ─── Public Status Page — status → colour/icon/label ─────────────────
//
// THE single mapping from a StatusComponentStatus to everything the page paints
// for it. Colours are TOKEN NAMES, never hex: `var(--accent-success)` resolves
// through the C&B brand override at the top of status.css (which itself sits on
// top of index.css, imported verbatim), so the public page tracks one palette
// with zero drift and zero duplicated colour values.
//
// The four semantic accents are used rather than the theme accent on purpose:
// "everything is fine" must not change colour when someone re-themes PCP.
//
// WHY TWO COLOURS PER LEVEL
// A status colour does two jobs that a single value cannot do on a light page:
//
//   color  the INK — icon strokes, the timeline marker, pill text via the
//          .sp-pill--* classes. It sits on a near-white card or on its own 12%
//          tint, so it has to clear 4.5:1 there. That forces a dark value.
//   fill   the BLOCK — a day cell in the 90-day grid, a legend swatch, the
//          tooltip dot. These are large solid areas carrying the brand's own
//          hues: available is cb.dk's green-teal and degraded its tan. As INK
//          those two would be ~2.1:1 and unreadable, which is why the tier
//          exists at all.
//
//          A fill is still a GRAPHICAL OBJECT, so it owes 3:1 against the card
//          it sits on. The brand's raw #4cb596 measures 2.33:1 there and the
//          raw tan 2.53:1, so both are walked DOWN their own hue (saturation
//          untouched) until they clear it — #419d82 and #bb8238, each 3.06:1.
//          Same hue family, same brand reading, legible. Do not "restore" the
//          raw brand hexes here: that silently drops the strip below AA.
//
// Every consumer picks one deliberately. Nothing paints a block with the ink or
// strokes an icon with the fill.

import {
  STATUS_COMPONENT_STATUS_LABELS_DA,
  STATUS_COMPONENT_STATUS_RANK,
  STATUS_HISTORY_CHAR_STATUS,
  type StatusComponentStatus,
} from '@pcp/shared';
import type { SIconName } from '../ui/SIcon';

export interface StatusLevel {
  /** INK — icon strokes and small markers, on a card or a soft tint. `var(--…)`. */
  color: string;
  /** BLOCK fill — day cells, legend swatches, the tooltip dot. `var(--…)`. */
  fill: string;
  /** Tinted background for pills, banners and soft chips. */
  soft: string;
  icon: SIconName;
  /** Danish, from the shared vocabulary. */
  label: string;
  /** Suffix for the BEM-ish modifier classes in status.css (`.sp-pill--available`). */
  modifier: string;
}

/**
 * INFORMATION — an announcement that applied to a system on a given day.
 *
 * Deliberately NOT a fifth STATUS_LEVELS entry: that map is keyed by
 * StatusComponentStatus, and information is not a status a component can be in.
 * It rides a separate per-day mask (PublicStatusComponent.info) and is painted
 * only where the day is otherwise normal, so a real fault is never hidden behind
 * a notice.
 */
export const INFO_LEVEL: StatusLevel = {
  color: 'var(--accent-info)',
  fill: 'var(--sp-fill-information)',
  soft: 'var(--accent-info-soft)',
  icon: 'info',
  label: 'Information',
  modifier: 'information',
};

export const STATUS_LEVELS: Record<StatusComponentStatus, StatusLevel> = {
  available: {
    color: 'var(--accent-success)',
    fill: 'var(--sp-fill-available)',
    soft: 'var(--accent-success-soft)',
    icon: 'operational',
    label: STATUS_COMPONENT_STATUS_LABELS_DA.available,
    modifier: 'available',
  },
  degraded: {
    color: 'var(--accent-warning)',
    fill: 'var(--sp-fill-degraded)',
    soft: 'var(--accent-warning-soft)',
    icon: 'degraded',
    label: STATUS_COMPONENT_STATUS_LABELS_DA.degraded,
    modifier: 'degraded',
  },
  maintenance: {
    color: 'var(--accent-info)',
    fill: 'var(--sp-fill-maintenance)',
    soft: 'var(--accent-info-soft)',
    icon: 'maintenance',
    label: STATUS_COMPONENT_STATUS_LABELS_DA.maintenance,
    modifier: 'maintenance',
  },
  unavailable: {
    color: 'var(--accent-error)',
    fill: 'var(--sp-fill-unavailable)',
    soft: 'var(--accent-error-soft)',
    icon: 'major',
    label: STATUS_COMPONENT_STATUS_LABELS_DA.unavailable,
    modifier: 'unavailable',
  },
};

/**
 * A day (or a component) we simply have no reading for — the history char '-',
 * or a day before the component existed. Deliberately NEUTRAL, never green: an
 * absent measurement is not a passing measurement.
 */
export const NO_DATA_LEVEL: StatusLevel = {
  color: 'var(--border-strong)',
  fill: 'var(--border-strong)',
  soft: 'var(--bg-tertiary)',
  icon: 'unknown',
  label: 'Ingen data',
  modifier: 'nodata',
};

export function levelForStatus(status: StatusComponentStatus | null | undefined): StatusLevel {
  return status ? (STATUS_LEVELS[status] ?? NO_DATA_LEVEL) : NO_DATA_LEVEL;
}

/**
 * One history character → its status. The lookup CAN be undefined ('-' means no
 * data, and an unrecognised char from a future payload version must degrade the
 * same way rather than throw) — hence the `?? null`.
 */
export function statusForHistoryChar(char: string): StatusComponentStatus | null {
  return STATUS_HISTORY_CHAR_STATUS[char] ?? null;
}

/** One history character → what to paint for it. */
export function levelForHistoryChar(char: string): StatusLevel {
  return levelForStatus(statusForHistoryChar(char));
}

/**
 * Collapse many components' history strings into ONE page-level strip:
 * worst-wins per day, exactly like the server's component → group → page
 * rollup. A day where every component has '-' stays '-' rather than becoming a
 * green day nobody measured.
 */
export function aggregateHistory(histories: readonly string[], days: number): string {
  if (days <= 0) return '';
  const out: string[] = new Array(days).fill('-');
  for (let day = 0; day < days; day += 1) {
    let worst: StatusComponentStatus | null = null;
    for (const history of histories) {
      const status = statusForHistoryChar(history.charAt(day));
      if (!status) continue;
      if (!worst || STATUS_COMPONENT_STATUS_RANK[status] > STATUS_COMPONENT_STATUS_RANK[worst]) worst = status;
    }
    out[day] = worst ? historyCharFor(worst) : '-';
  }
  return out.join('');
}

/** Inverse of statusForHistoryChar, kept local so aggregateHistory can rebuild
 *  a well-formed history string that every other reader understands. */
function historyCharFor(status: StatusComponentStatus): string {
  switch (status) {
    case 'available':
      return 'o';
    case 'degraded':
      return 'd';
    case 'maintenance':
      return 'n';
    case 'unavailable':
      return 'm';
    default:
      return '-';
  }
}

/**
 * Fraction of MEASURED days that were fully available, or null when nothing was
 * measured at all (so the caller renders "Ingen data" instead of "0 % oppetid").
 *
 * Only 'available' counts as up. Degraded, maintenance and unavailable days all
 * count against the figure — a stricter number can only ever understate how well
 * we did, and a status page that overstates its own uptime is worthless.
 */
export function uptimeRatio(history: string): number | null {
  let measured = 0;
  let up = 0;
  for (const char of history) {
    const status = statusForHistoryChar(char);
    if (!status) continue;
    measured += 1;
    if (status === 'available') up += 1;
  }
  if (measured === 0) return null;
  return up / measured;
}
